import {
  CheckoutAppSelector,
  ConsumerLendingState,
  useAppSelector,
  useCashAppPay,
  useCheckoutState,
  useCohort,
  useConsumerLendingEligible,
  useHasUserInitiatedChangedPaymentMethod,
  useOfferDetailPBI,
  usePreferredBankAccount,
  usePreferredCard,
  usePreferredPaymentMethod,
  useShouldSetApplePayAsPurchaseMethod,
  useShouldShowCashAppTooltip,
  useConsumerSelector,
  useCountry,
  usePaymentTypeAllowInConvergence,
  useIsAfterpayFinanceFeeEligible,
  useEffectiveRoutingDecision,
  useIsPclOnlyOfferPage,
  useRoutingDecision,
  useConsumerFeatureFlagReady,
  useReadyFeatureFlag,
  useIsCountry,
} from '..'
import { useEffect, useMemo, useState } from 'react'
import { createSelector } from '@reduxjs/toolkit'
import {
  Features,
  PaymentMethodType,
  PaymentOption,
  PaymentOptionsError,
  PaymentOptionsStatus,
  SelectedPaymentMethod,
  TerminalErrorCode,
  DEFAULT_PAYMENT_TYPE_SORT_ORDER,
} from '../../model'

import { Country, IdentityCheckStatus, PaymentType, Feature } from '@afterpay/types'
import { useFlag } from '@afterpaytouch/feature'
import { Cohort, ErrorCode } from '@afterpaytouch/portal-api'

import { CLRiskDeclineError } from './types'

import { getFeatureFromPaymentType } from '../../utils/payment-type'
import { app } from '../../env'

/* Selectors */

export const consumerLendingSelector: CheckoutAppSelector<ConsumerLendingState> = (state) => state.consumerLending

export const selectedPaymentTypeSelector: CheckoutAppSelector<PaymentType | undefined> = createSelector(
  consumerLendingSelector,
  (state) => state.selectedPaymentType
)

export const paymentOptionsStatusSelector: CheckoutAppSelector<PaymentOptionsStatus> = createSelector(
  consumerLendingSelector,
  (state) => state.paymentOptionsStatus
)

export const selectedPaymentOptionSelector: CheckoutAppSelector<PaymentOption | undefined> = createSelector(consumerLendingSelector, (state) =>
  state.paymentOptions.find((paymentOption) => state.selectedPaymentOptionId === paymentOption.id)
)

export const selectedAutopayEnabledSelector: CheckoutAppSelector<boolean | undefined> = createSelector(
  consumerLendingSelector,
  (state) => state.isAutopayEnabled
)

export const paymentOptionsSelector: CheckoutAppSelector<PaymentOption[]> = createSelector(consumerLendingSelector, (state) => state.paymentOptions)

export const pclPaymentOptionsSelector: CheckoutAppSelector<PaymentOption[]> = createSelector(consumerLendingSelector, (state) =>
  state.paymentOptions.filter((paymentOption) => paymentOption.paymentType === PaymentType.PCL)
)

export const pbiPaymentOptionSelector: CheckoutAppSelector<PaymentOption | undefined> = createSelector(consumerLendingSelector, (state) =>
  state.paymentOptions.find((paymentOption) => paymentOption.paymentType === PaymentType.PBI)
)

export const paymentOptionsErrorSelector: CheckoutAppSelector<PaymentOptionsError | undefined> = createSelector(
  consumerLendingSelector,
  (state) => state?.error
)

export const creditFreezeRetryContextSelector: CheckoutAppSelector<ConsumerLendingState['creditFreezeRetryContext']> = createSelector(
  consumerLendingSelector,
  (state) => state?.creditFreezeRetryContext
)

export const selectedLoanAgreementIdSelector: CheckoutAppSelector<string | undefined> = createSelector(
  consumerLendingSelector,
  (state) => state.loanAgreementDocumentId
)

export const selectedPaymentAuthorisationIdSelector: CheckoutAppSelector<string | undefined> = createSelector(
  consumerLendingSelector,
  (state) => state.paymentAuthorisationDocumentId
)

export const creditScoreDisclosureIdSelector: CheckoutAppSelector<string | undefined> = createSelector(
  consumerLendingSelector,
  (state) => state.creditScoreDisclosureDocumentId
)

export const showEfaModalSelector: CheckoutAppSelector<boolean> = createSelector(consumerLendingSelector, (state) => state.showEfaModal)

/* React Hooks */

export const useConsumerLendingState = (): ConsumerLendingState => useAppSelector(consumerLendingSelector)

export const useSelectedPaymentType = (): PaymentType | undefined => useAppSelector(selectedPaymentTypeSelector)

export const useSelectedPaymentOption = (): PaymentOption | undefined => useAppSelector(selectedPaymentOptionSelector)

export const useIsConsumerLendingOrder = (): boolean => {
  const selectedPaymentOption = useSelectedPaymentOption()
  return selectedPaymentOption !== undefined && PaymentType.PCL === selectedPaymentOption.paymentType
}

export const usePaymentOptionsStatus = (): PaymentOptionsStatus => useAppSelector(paymentOptionsStatusSelector)

// @ts-expect-error: OPERATION BLEED STOPPER
export const useIsConsumerLendingAutopayEnabled = (): boolean => useAppSelector(selectedAutopayEnabledSelector)

export const usePaymentOptions = (): PaymentOption[] => useAppSelector(paymentOptionsSelector)

export const usePclPaymentOptions = (): PaymentOption[] => useAppSelector(pclPaymentOptionsSelector)

// @ts-expect-error: OPERATION BLEED STOPPER
export const usePbiPaymentOption = (): PaymentOption => useAppSelector(pbiPaymentOptionSelector)

export const usePaymentOptionsError = (): PaymentOptionsError | undefined => useAppSelector(paymentOptionsErrorSelector)

export const useCreditFreezeRetryContext = (): ConsumerLendingState['creditFreezeRetryContext'] => useAppSelector(creditFreezeRetryContextSelector)

export const useConsumerLendingStartDate = (): string => {
  const { started } = useCheckoutState()
  const paymentOption = useSelectedPaymentOption()
  // @ts-expect-error: OPERATION BLEED STOPPER
  const hasDownPayment = parseFloat(paymentOption.amountDueToday.amount) > 0
  // @ts-expect-error: OPERATION BLEED STOPPER
  return started.paymentSchedules[hasDownPayment ? 1 : 0].installmentDueDate
}
// @ts-expect-error: OPERATION BLEED STOPPER
export const useLoanAgreementDocumentId = (): string => useAppSelector(selectedLoanAgreementIdSelector)
// @ts-expect-error: OPERATION BLEED STOPPER
export const usePaymentAuthorisationDocumentId = (): string => useAppSelector(selectedPaymentAuthorisationIdSelector)
// @ts-expect-error: OPERATION BLEED STOPPER
export const useCreditScoreDisclosureDocumentId = (): string => useAppSelector(creditScoreDisclosureIdSelector)

export const useShowEfaModal = (): boolean => useAppSelector(showEfaModalSelector)

export const usePaymentOptionsHasPCL = (): boolean => {
  const paymentOptionsStatus = usePaymentOptionsStatus()
  const paymentOptions = usePaymentOptions()

  return paymentOptionsStatus === PaymentOptionsStatus.Fetched && paymentOptions.some((po) => po.paymentType === PaymentType.PCL)
}

export const useShowSummaryChangeButton = (): boolean => {
  const paymentOptionsError = usePaymentOptionsError()
  const paymentOptionsStatus = usePaymentOptionsStatus()
  const paymentOptions = usePaymentOptions()
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const { isError: isCLRiskError } = useCLRiskDeclineError()
  const isConsumerLendingAllowInConvergence = usePaymentTypeAllowInConvergence(PaymentType.PCL)

  return (
    // show button if user is eligible for consumer lending
    isConsumerLendingEligible &&
    // and if payment-options have been fetched and there are no PCL payment options
    // otherwise, skip this check if payment options have not yet been fetched
    ((paymentOptionsStatus === PaymentOptionsStatus.Fetched && paymentOptions.some((po) => po.paymentType === PaymentType.PCL)) ||
      paymentOptionsStatus !== PaymentOptionsStatus.Fetched) &&
    // and if user hasn't hit max attempts on the IdentityVerification form
    paymentOptionsError === undefined &&
    // and if user was not already declined for pay monthly
    !isCLRiskError &&
    isConsumerLendingAllowInConvergence
  )
}

export const usePBIRejected = (): boolean => {
  const offerDetailPBI = useOfferDetailPBI()
  return Boolean(offerDetailPBI?.rejectedReasonCode)
}

export const useAppropriatePaymentMethod = (): SelectedPaymentMethod | null => {
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const preferredBankAccount = usePreferredBankAccount()
  const preferredCard = usePreferredCard()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const cashAppPay = useCashAppPay()
  const { shouldOverrideCashAppPay } = useShouldShowCashAppTooltip()
  const selectedPaymentOption = useSelectedPaymentOption()
  const userInitiatedChangedPaymentMethod = useHasUserInitiatedChangedPaymentMethod()

  const account = preferredBankAccount?.account ?? null
  const card = preferredCard?.card ?? null
  const isConsumerLending = selectedPaymentOption !== undefined && selectedPaymentOption.paymentType === PaymentType.PCL

  if (userInitiatedChangedPaymentMethod) {
    // if user has manually changed payment method
    // should only be triggered from PaymentOptionsModal flow
    return null
  }

  // card should have higher priority than other payment methods
  // use preferred card for PCL orders if they have another blocked payment method, even card is invalid
  if (cashAppPay.length > 0 && (preferredPaymentMethod === PaymentMethodType.CASH_APP_PAY || shouldOverrideCashAppPay)) {
    if (isConsumerLending && card !== null) {
      return { type: PaymentMethodType.CREDIT_CARD, card: { token: card.token } }
    }
    return { type: PaymentMethodType.CASH_APP_PAY, cashAppPay: cashAppPay[0] }
  } else if (shouldUseApplePay) {
    if (isConsumerLending && card !== null) {
      return { type: PaymentMethodType.CREDIT_CARD, card: { token: card.token } }
    }
    return { type: PaymentMethodType.APPLE_PAY }
  } else if (preferredPaymentMethod === PaymentMethodType.ACH_BANK_ACCOUNT && isConsumerLending && card !== null) {
    // use preferred card for PCL orders if they have a card instead of ach bank account.
    // shouldn't happen on PaymentTypes page since selectedPaymentOption would be undefined at this stage
    return { type: PaymentMethodType.CREDIT_CARD, card: { token: card.token } }
  } else if (preferredPaymentMethod === PaymentMethodType.ACH_BANK_ACCOUNT && account !== null) {
    return { type: PaymentMethodType.ACH_BANK_ACCOUNT, account }
  } else if (preferredPaymentMethod === PaymentMethodType.CREDIT_CARD && card !== null) {
    return { type: PaymentMethodType.CREDIT_CARD, card: { token: card.token } }
  } else if (cashAppPay.length > 0) {
    // fallback to cashapp
    return { type: PaymentMethodType.CASH_APP_PAY, cashAppPay: cashAppPay[0] }
  } else if (card !== null) {
    // fallback to card
    return { type: PaymentMethodType.CREDIT_CARD, card: { token: card.token } }
  } else {
    // handle the no eligible payment method scenario by returning null
    return null
  }
}

export const useCLRiskDeclineError = (): CLRiskDeclineError => {
  const paymentOptionsError = usePaymentOptionsError()
  const isPaymentOptionsError = paymentOptionsError !== undefined

  return {
    isError: isPaymentOptionsError,
    error: paymentOptionsError,
  }
}

export const useShowIdvModal = (): boolean => {
  const bypassIdvFlag = useFlag(Features.BYPASS_IDV_FOR_CONSUMER_LENDING_ORDERS)
  const { identityCheckStatuses, shadowAccountExists } = useConsumerSelector()
  const cohort = useCohort()

  // Bypass IDV for:
  // 1. Environments where we need to bypass IDV (e.g. Sandbox) - should be disabled in prod
  // 2. Shadow account customers (CashOnly cohort) who perform IDV externally
  const canBypassIDVForConsumerLendingOrders = bypassIdvFlag || (cohort === Cohort.CashOnly && shadowAccountExists === true)

  return (
    !canBypassIDVForConsumerLendingOrders &&
    !(identityCheckStatuses?.PCL === IdentityCheckStatus.VERIFIED || identityCheckStatuses?.PCL === IdentityCheckStatus.NOT_REQUIRED)
  )
}

export const useIsPayMonthlyOnly = (): boolean => {
  const offerDetailPBI = useOfferDetailPBI()
  const country = useCountry()

  return offerDetailPBI?.rejectedReasonCode === ErrorCode.MerchantPayMonthlyOnly && country === Country.US
}

export const useIsExtendedFraudAlert = (): boolean => {
  const paymentOptionsError = usePaymentOptionsError()
  const country = useCountry()

  return paymentOptionsError?.errorCode === ErrorCode.ExtendedFraudAlert && country === Country.US
}

export const useIsCreditFreezeDeclineFlow = (): boolean => {
  const paymentOptionsError = usePaymentOptionsError()
  const country = useCountry()

  return (
    paymentOptionsError?.error === TerminalErrorCode.CreditFreezeDeclinedPCL &&
    country === Country.US &&
    paymentOptionsError?.extra?.creditFreezeApplicationDeadline !== undefined
  )
}

export const usePreselectedPaymentOption = (): PaymentOption | undefined => {
  const paymentOptions = usePaymentOptions()

  if (paymentOptions.find((po) => po.rank) == null) {
    return undefined
  }

  return [...paymentOptions].sort((a, b) => (a?.rank ?? Infinity) - (b?.rank ?? Infinity))[0]
}

export const useSelectedFeature = (): Feature => {
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const paymentType = useSelectedPaymentType() ?? PaymentType.PBI
  return getFeatureFromPaymentType({ paymentType, isAFF })
}

const VALID_PAYMENT_TYPES = new Set<string>(Object.values(PaymentType))

const parsePaymentTypeSortOrder = (value: string | undefined): PaymentType[] | undefined => {
  if (!value) return undefined
  const types = value
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is PaymentType => VALID_PAYMENT_TYPES.has(s))
  return types.length > 0 ? types : undefined
}

const useLdPaymentTypeSortOrder = (): PaymentType[] | null => {
  const ldValue = useFlag<PaymentType[] | null>(Features.PAYMENT_TYPE_SORTING, null)
  if (ldValue != null && Array.isArray(ldValue) && ldValue.length > 0) return ldValue
  return null
}

export const useIsInlinePaymentOptions = (): boolean => {
  const hasLdSortOrder = useLdPaymentTypeSortOrder() !== null
  const routingDecision = useEffectiveRoutingDecision()

  return hasLdSortOrder || routingDecision === 'ONE_OFFER_PAGE' || routingDecision === 'ONE_OFFER_PAGE_PCL_ONLY'
}

export const usePaymentTypeSortOrder = (): PaymentType[] => {
  return useLdPaymentTypeSortOrder() ?? parsePaymentTypeSortOrder(app.PAYMENT_TYPE_SORTING) ?? DEFAULT_PAYMENT_TYPE_SORT_ORDER
}

/**
 * Umbrella gate for the AU Pay Monthly checkout experience. Read with useReadyFeatureFlag so a stale
 * pre-identify LD value can't flash the wrong variant on refresh, and ANDed with the country in
 * code so a mis-targeted flag can't leak AU-only CX (e.g. non-TILA CTA copy) into other regions.
 */
export const useIsAuPayMonthly = (): boolean => {
  const isEnabled = useReadyFeatureFlag<boolean>(Features.AU_PAY_MONTHLY, false)
  const isAu = useIsCountry(Country.AU)
  return isEnabled && isAu
}

// CASHDAP-3449: on the PCL-only one offer page the PBI radio is hidden entirely rather than
// rendered disabled — the server has already ruled PBI out non-recoverably.
export const useVisiblePaymentTypes = (): PaymentType[] => {
  const paymentTypeSortOrder = usePaymentTypeSortOrder()
  const isPclOnlyOfferPage = useIsPclOnlyOfferPage()

  return useMemo(
    () => (isPclOnlyOfferPage ? paymentTypeSortOrder.filter((type) => type !== PaymentType.PBI) : paymentTypeSortOrder),
    [isPclOnlyOfferPage, paymentTypeSortOrder]
  )
}

// LD identify normally resolves well under a second; the cap only exists so a failed or
// hung identify (whose onDone never fires, leaving hasFeatureFlagsReady false forever)
// degrades to legacy routing instead of an indefinite hold.
const LD_READY_MAX_WAIT_MS = 5000

/**
 * True while routing (or routing-dependent rendering) should wait for LaunchDarkly
 * readiness. The raw `/start` routing decision lands in the store before LD identify
 * completes, but `useEffectiveRoutingDecision` only honours it once
 * `hasFeatureFlagsReady` is true — routing before that point falls through to the legacy
 * cascade and can land a decision-cohort session on the wrong surface (e.g. a PCL-only
 * user on /payment-types) with no later correction. Sessions without a routing decision
 * never hold.
 */
export const useHoldRoutingForLdReady = (): boolean => {
  const routingDecision = useRoutingDecision()
  const hasFeatureFlagsReady = useConsumerFeatureFlagReady()
  const [ldReadyWaitExpired, setLdReadyWaitExpired] = useState(false)

  const shouldHold = routingDecision !== undefined && !hasFeatureFlagsReady && !ldReadyWaitExpired

  useEffect(() => {
    if (!shouldHold) {
      return
    }
    const timer = setTimeout(() => setLdReadyWaitExpired(true), LD_READY_MAX_WAIT_MS)
    return () => clearTimeout(timer)
  }, [shouldHold])

  return shouldHold
}
